<template>
  <v-col cols="12">
    <dynamic-data-table
      :can-delete="true"
      :is-loading="tokenStatus === 'loading'"
      :table-headers="headers"
      :table-records="tokens"
      table-title="Tokens"
      @deleteItem="displayDeleteDialog"
      @reloadData="fetchTokens"
    >
      <template #table-actions>
        <v-dialog key="add-token" v-model="tokenDialog" max-width="500px">
          <template #activator="{ on }">
            <v-tooltip bottom>
              <template #activator="{ on: onTooltip }">
                <v-icon class="ant-icon" dense v-on="{ ...on, ...onTooltip }">
                  mdi-plus
                </v-icon>
              </template>
              <span>New token</span>
            </v-tooltip>
          </template>

          <v-card>
            <v-card-title class="justify-center text-uppercase headline">
              Create token
            </v-card-title>
            <v-divider />
            <v-form ref="form" @submit.prevent>
              <div class="px-10 pb-5">
                <ant-input :is-optional="false" label="Name">
                  <template #input-field>
                    <v-text-field
                      v-model="token.app_name"
                      label="App name"
                      :rules="[rules.required]"
                      dense
                      filled
                      hide-details
                      single-line
                      @input="error = false"
                    />
                  </template>
                </ant-input>
              </div>
              <v-card-actions class="ant-border-top ant-dialog-actions-bg">
                <v-spacer />
                <v-btn
                  color="error"
                  small
                  text
                  @click="
                    tokenDialog = false;
                    token = {};
                  "
                >
                  Cancel
                </v-btn>
                <v-btn color="primary" elevation="0" small @click="createToken">
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </template>
    </dynamic-data-table>

    <!-- Delete confirm -->
    <delete-dialog
      :dialog="tokenDeleteDialog"
      :title="`Remove token “${tokenToDelete.app_name || ''}” ?`"
      @closeDialog="
        tokenDeleteDialog = false;
        tokenToDelete = {};
      "
      @deleteAction="deleteToken"
    />

    <!-- Compact 'Copy credentials' dialog -->
    <v-dialog v-model="secretDialog" max-width="420px" persistent>
      <v-card class="pa-4">
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="text-subtitle-1 font-weight-medium">Copy credentials</div>
          <v-btn icon small @click="closeSecretDialog"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </div>

        <v-alert type="warning" dense class="mb-4">
          Save your client secret now — you won’t see it again.
        </v-alert>

        <v-text-field
          label="Client ID"
          :value="created.client_id"
          readonly
          dense
          filled
          hide-details
          append-icon="mdi-content-copy"
          class="mb-3"
          @click:append="copy(created.client_id)"
        />

        <v-text-field
          :type="reveal ? 'text' : 'password'"
          label="Client Secret"
          :value="created.client_secret"
          readonly
          dense
          filled
          hide-details
          :append-icon="reveal ? 'mdi-eye-off' : 'mdi-eye'"
          append-outer-icon="mdi-content-copy"
          class="mb-2"
          @click:append="reveal = !reveal"
          @click:append-outer="copy(created.client_secret)"
        />

        <div class="caption grey--text text--darken-1 mb-2">
          Tip: click the copy icons to place values on your clipboard.
        </div>

        <v-card-actions class="pa-0 mt-2">
          <v-spacer />
          <v-btn color="primary" small elevation="0" @click="closeSecretDialog">
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { mapGetters } from 'vuex';
import DeleteDialog from '../DeleteDialog';
import DynamicDataTable from '@/components/DynamicDataTable';
import AntInput from '@/components/AntInput.vue';

export default {
  name: 'Tokens',
  components: { AntInput, DynamicDataTable, DeleteDialog },
  data() {
    return {
      headers: [
        { text: 'Name', value: 'app_name' },
        { text: 'Client ID', value: 'client_id' },
        { text: '', value: 'actions', width: '1%', sortable: false },
      ],
      rules: {
        required: (value) => !!value || 'Required.',
      },
      tokenDialog: false,
      tokenDeleteDialog: false,
      tokenToDelete: {},
      token: {},
      secretDialog: false,
      created: { client_id: '', client_secret: '' },
      reveal: false,
    };
  },
  computed: {
    ...mapGetters(['tokens', 'tokenStatus']),
  },
  mounted() {
    if (this.$route.query.status !== 'reset') {
      this.fetchTokens();
    }
  },
  methods: {
    fetchTokens() {
      this.$store.dispatch('fetchTokens');
    },
    displayDeleteDialog(item) {
      this.tokenToDelete = item;
      this.tokenDeleteDialog = true;
    },
    deleteToken() {
      this.$store.dispatch('deleteToken', this.tokenToDelete).then(() => {
        this.tokenDeleteDialog = false;
        this.tokenToDelete = {};
      });
    },
    async createToken() {
      if (!this.$refs.form.validate()) return;
      // Action returns { client_id, client_secret, token_uuid, app_name }
      const payload = await this.$store.dispatch('createToken', this.token);

      this.created = {
        client_id: payload.client_id,
        client_secret: payload.client_secret,
      };

      this.tokenDialog = false;
      this.token = {};
      this.$refs.form.reset();

      this.reveal = false;
      this.secretDialog = true;
    },
    copy(text) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      this.$store.commit('showNotification', {
        content: 'Copied!',
        color: 'success',
      });
    },
    closeSecretDialog() {
      this.secretDialog = false;
      this.created = { client_id: '', client_secret: '' };
    },
  },
};
</script>

<style scoped>
/* keep it minimal & compact */
</style>
